/**
 * ADR-025 B-4 adaptive question workspace.
 *
 * This is the flag-ON replacement for QuestionDetailModal: a page surface
 * below 1024px and the detail half of a queue+workspace split at wider
 * app-render widths. Contextual tools mount only after their section is
 * opened. The image lightbox overlays the workspace through A-0, so the
 * question text remains mounted behind it.
 */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Box, Button, Flex, FlexItem, Panel, Text } from '@bigcommerce/big-design';
import {
  ArrowBackIcon,
  CheckIcon,
  CloseIcon,
  ExpandLessIcon,
  ExpandMoreIcon,
  RefreshIcon,
} from '@bigcommerce/big-design-icons';
import type { AdminQuestionListItem } from '@find-my-part/types';
import { FILE_VALIDATION } from '@find-my-part/types';
import { AccessibleModal } from '../common/AccessibleModal';
import { AdminImageThumbnail } from '../Media/AdminImageThumbnail';
import { AdminImageUploader } from '../Media/AdminImageUploader';
import { MediaStatusBadge } from '../Media/MediaStatusBadge';
import { useQuestionMedia } from '../../hooks/useQuestionMedia';
import { formatDate } from '../../utils/formatters';
import { QualityBadge } from './QualityBadge';
import { QuestionChangesTimeline } from './QuestionChangesTimeline';
import { ReplyModerationPanel } from './ReplyModerationPanel';
import { RfiManagementPanel } from './Rfi/RfiManagementPanel';
import { StatusBadge } from './StatusBadge';
import {
  QuestionStatusWriteNotice,
  type QuestionStatusWriteIssue,
} from './QuestionStatusWriteNotice';
import { tokens } from '../../tokens';

export interface QuestionWorkspaceHandle {
  /**
   * Route a subject/section/close transition through the live reply-draft
   * boundary. The callback runs immediately when clean, or only after the
   * operator explicitly discards a non-empty rejection draft.
   */
  requestTransition: (proceed: () => void) => void;
}

export interface QuestionWorkspaceProps {
  question: AdminQuestionListItem;
  token: string;
  onClose: () => void;
  onApprove: (questionId: string) => Promise<boolean | void>;
  onReject: (questionId: string) => Promise<void>;
  pendingActions?: Set<string>;
  statusWriteIssue?: QuestionStatusWriteIssue | null;
  onStatusWriteReapply?: () => void;
  onStatusWriteRefresh?: () => void;
  onStatusWriteDismiss?: () => void;
  onDraftDirtyChange?: (dirty: boolean) => void;
  /**
   * True when AdminShell owns the same transition guard. Prevents a Back/close
   * action from asking the workspace twice before the shell commits.
   */
  externalNavigationGuard?: boolean;
  /**
   * The shell keep-alives the selected workspace while another section is
   * visible. Heavy contextual consumers unmount while inactive so retained
   * selection does not retain authenticated image resources or background
   * request surfaces.
   */
  isActive?: boolean;
  testId?: string;
}

interface ContextSectionProps {
  id: string;
  title: string;
  summary: string;
  children: React.ReactNode;
  collapseDisabled?: boolean;
  isActive?: boolean;
  testId?: string;
}

function ContextSection({
  id,
  title,
  summary,
  children,
  collapseDisabled = false,
  isActive = true,
  testId,
}: ContextSectionProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);

  const toggle = (): void => {
    if (expanded && collapseDisabled) return;
    setExpanded((value) => !value);
  };

  const regionId = `question-workspace-section-${id}`;

  return (
    <Box
      marginTop="small"
      style={{
        border: `1px solid ${tokens.color.border.default}`,
        borderRadius: tokens.dimension.radius.lg,
        overflow: 'hidden',
      }}
      data-testid={testId}
    >
      <Button
        variant="subtle"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={regionId}
        disabled={expanded && collapseDisabled}
        iconRight={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        {title}
      </Button>
      <Box paddingHorizontal="small" paddingBottom="small">
        <Text color="secondary" marginBottom="none">
          {summary}
        </Text>
      </Box>
      <div id={regionId} role="region" aria-label={title} hidden={!expanded || !isActive}>
        {expanded && isActive && (
          <Box
            padding="medium"
            style={{
              borderTop: `1px solid ${tokens.color.border.default}`,
              minWidth: 0,
              overflowWrap: 'anywhere',
            }}
          >
            {children}
          </Box>
        )}
      </div>
    </Box>
  );
}

interface QuestionImagesWorkspaceProps {
  questionId: string;
  token: string;
  testId?: string;
}

function QuestionImagesWorkspace({
  questionId,
  token,
  testId,
}: QuestionImagesWorkspaceProps): React.ReactElement {
  const { media, isLoading, isMutating, refresh, uploadImage, removeImage, setVisibility } =
    useQuestionMedia({ questionId, token, enabled: true });
  const [preview, setPreview] = useState<{ url: string; label: string } | null>(null);

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" flexGap="0.5rem">
        <FlexItem>
          <Text bold marginBottom="none">
            Question images {media ? `(${media.length})` : ''}
          </Text>
        </FlexItem>
        <FlexItem>
          <Button
            variant="subtle"
            onClick={() => void refresh()}
            disabled={isLoading || isMutating}
            iconLeft={<RefreshIcon />}
            data-testid={testId ? `${testId}-refresh` : undefined}
          >
            Refresh images
          </Button>
        </FlexItem>
      </Flex>

      {isLoading && media === null && <Text color="secondary">Loading images…</Text>}
      {!isLoading && media === null && (
        <Text color="danger">Images are unavailable. Use Refresh images to try again.</Text>
      )}
      {media && media.length === 0 && (
        <Text color="secondary">No images are attached to this question.</Text>
      )}
      {media && media.length > 0 && (
        <Flex
          flexWrap="wrap"
          flexGap="0.75rem"
          marginTop="small"
          role="group"
          aria-label={`Images attached to this question (${media.length})`}
        >
          {media.map((item, index) => {
            const label = `Photo ${index + 1} — ${item.originalFilename ?? item.attachmentId}`;
            return (
              <FlexItem key={item.attachmentId} style={{ maxWidth: '120px' }}>
                <AdminImageThumbnail
                  src={item.url}
                  alt={label}
                  token={token}
                  onClick={(url) => setPreview({ url, label })}
                  size={96}
                  testId={testId ? `${testId}-thumb-${item.attachmentId}` : undefined}
                />
                <Box marginTop="xxSmall">
                  <MediaStatusBadge status={item.moderationStatus} />
                </Box>
                {item.legalHold && (
                  <Text color="secondary" marginBottom="none">
                    Legal hold
                  </Text>
                )}
                <Flex flexDirection="column" alignItems="stretch" marginTop="xxSmall">
                  {item.moderationStatus === 'APPROVED' && (
                    <Button
                      variant="subtle"
                      disabled={isMutating}
                      onClick={() => void setVisibility(item.attachmentId, 'HIDE')}
                      data-testid={testId ? `${testId}-hide-${item.attachmentId}` : undefined}
                    >
                      Hide
                    </Button>
                  )}
                  {item.moderationStatus === 'HIDDEN' && !item.legalHold && (
                    <Button
                      variant="subtle"
                      disabled={isMutating}
                      onClick={() => void setVisibility(item.attachmentId, 'SHOW')}
                      data-testid={testId ? `${testId}-show-${item.attachmentId}` : undefined}
                    >
                      Show
                    </Button>
                  )}
                  <Button
                    variant="subtle"
                    actionType="destructive"
                    disabled={isMutating || item.legalHold}
                    title={
                      item.legalHold
                        ? 'Legal hold: this image is preserved and cannot be removed.'
                        : undefined
                    }
                    aria-label={`Remove image ${label}`}
                    onClick={() => void removeImage(item.attachmentId)}
                    data-testid={testId ? `${testId}-remove-${item.attachmentId}` : undefined}
                  >
                    Remove
                  </Button>
                </Flex>
              </FlexItem>
            );
          })}
        </Flex>
      )}

      <Box marginTop="small">
        <AdminImageUploader
          maxFiles={FILE_VALIDATION.MAX_IMAGES_PER_QUESTION}
          maxSizeMb={FILE_VALIDATION.MAX_SIZE_MB}
          allowedFormats={FILE_VALIDATION.ALLOWED_MIME_TYPES}
          onUpload={uploadImage}
          disabled={!token}
          testId={testId ? `${testId}-uploader` : undefined}
        />
      </Box>

      <AccessibleModal
        isOpen={preview !== null}
        onClose={() => setPreview(null)}
        closeOnEscKey
        closeOnClickOutside
        header={preview ? `Question image — ${preview.label}` : 'Question image'}
        actions={[
          {
            text: 'Close',
            variant: 'secondary',
            onClick: () => setPreview(null),
          },
        ]}
        data-testid={testId ? `${testId}-preview` : undefined}
      >
        {preview && (
          <Box style={{ textAlign: 'center' }}>
            <img
              src={preview.url}
              alt={preview.label}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: tokens.dimension.radius.sm,
              }}
            />
          </Box>
        )}
      </AccessibleModal>
    </>
  );
}

export const QuestionWorkspace = forwardRef<QuestionWorkspaceHandle, QuestionWorkspaceProps>(
  function QuestionWorkspace(
    {
      question,
      token,
      onClose,
      onApprove,
      onReject,
      pendingActions,
      statusWriteIssue = null,
      onStatusWriteReapply,
      onStatusWriteRefresh,
      onStatusWriteDismiss,
      onDraftDirtyChange,
      testId,
      externalNavigationGuard = false,
      isActive = true,
    },
    ref
  ): React.ReactElement {
    const headingRef = useRef<HTMLHeadingElement>(null);
    const pendingTransitionRef = useRef<(() => void) | null>(null);
    const [replyDraftDirty, setReplyDraftDirty] = useState(false);
    const [closeRequestNonce, setCloseRequestNonce] = useState(0);
    const [isApproving, setIsApproving] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const parentPending = pendingActions?.has(question.questionId) ?? false;
    const actionsPending = isApproving || isRejecting || parentPending;

    useEffect(() => {
      headingRef.current?.focus({ preventScroll: true });
    }, [question.questionId]);

    useEffect(() => {
      onDraftDirtyChange?.(replyDraftDirty);
    }, [onDraftDirtyChange, replyDraftDirty]);
    useEffect(() => () => onDraftDirtyChange?.(false), [onDraftDirtyChange]);

    // The shell guard covers semantic navigation. Browser reload/close is a
    // separate loss vector, so a live reply-rejection draft also participates
    // in the platform beforeunload confirmation.
    useEffect(() => {
      if (!replyDraftDirty) return undefined;
      const preventDraftLoss = (event: BeforeUnloadEvent): void => {
        event.preventDefault();
        event.returnValue = '';
      };
      window.addEventListener('beforeunload', preventDraftLoss);
      return () => window.removeEventListener('beforeunload', preventDraftLoss);
    }, [replyDraftDirty]);

    const requestTransition = useCallback(
      (proceed: () => void): void => {
        // The workspace owns any outcome UI for this mutation. Do not let
        // Back, shell navigation, or another imperative transition hide it
        // while the request can still return a conflict/ambiguous result.
        if (actionsPending) return;
        if (!replyDraftDirty) {
          proceed();
          return;
        }
        pendingTransitionRef.current = proceed;
        setCloseRequestNonce((nonce) => nonce + 1);
      },
      [actionsPending, replyDraftDirty]
    );

    useImperativeHandle(ref, () => ({ requestTransition }), [requestTransition]);

    const handleParentCloseResolve = useCallback(
      (proceed: boolean): void => {
        const transition = pendingTransitionRef.current;
        pendingTransitionRef.current = null;
        if (proceed && !actionsPending) transition?.();
      },
      [actionsPending]
    );

    const requestClose = (): void => {
      if (actionsPending) return;
      if (externalNavigationGuard) onClose();
      else requestTransition(onClose);
    };

    const handleApprove = (): void => {
      if (isApproving || isRejecting) return;
      requestTransition(() => {
        setIsApproving(true);
        void (async () => {
          try {
            const succeeded = await onApprove(question.questionId);
            if (succeeded !== false) onClose();
          } finally {
            setIsApproving(false);
          }
        })();
      });
    };

    const handleReject = (): void => {
      if (isApproving || isRejecting) return;
      requestTransition(() => {
        setIsRejecting(true);
        void (async () => {
          try {
            await onReject(question.questionId);
          } finally {
            setIsRejecting(false);
          }
        })();
      });
    };

    const canModerate = question.status === 'PENDING_REVIEW' || parentPending;

    return (
      <div
        data-testid={testId}
        data-question-workspace={question.questionId}
        style={{ minWidth: 0, maxWidth: '100%', overflowWrap: 'anywhere' }}
      >
        <Panel>
          <Box padding="medium">
            <Flex
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="wrap"
              flexGap="0.75rem"
            >
              <FlexItem style={{ minWidth: 0, flex: '1 1 280px' }}>
                <Button
                  variant="subtle"
                  onClick={requestClose}
                  disabled={actionsPending}
                  iconLeft={<ArrowBackIcon />}
                  data-status-action="back"
                  data-testid={testId ? `${testId}-back` : undefined}
                >
                  Back to questions
                </Button>
                <h2
                  ref={headingRef}
                  tabIndex={-1}
                  style={{
                    margin: `${tokens.dimension.space.md} 0 ${tokens.dimension.space.xs}`,
                    outline: 'none',
                    overflowWrap: 'anywhere',
                  }}
                  data-testid={testId ? `${testId}-heading` : undefined}
                >
                  {question.title}
                </h2>
                <Text color="secondary" marginBottom="none">
                  Question workspace
                </Text>
              </FlexItem>
              {canModerate && (
                <FlexItem>
                  <Flex flexWrap="wrap" flexGap="0.5rem">
                    <FlexItem>
                      <Button
                        variant="secondary"
                        actionType="destructive"
                        onClick={handleReject}
                        disabled={actionsPending}
                        isLoading={isRejecting}
                        iconLeft={!isRejecting ? <CloseIcon /> : undefined}
                        data-testid={testId ? `${testId}-reject` : undefined}
                      >
                        Reject
                      </Button>
                    </FlexItem>
                    <FlexItem>
                      <Button
                        variant="primary"
                        onClick={handleApprove}
                        disabled={actionsPending}
                        isLoading={isApproving}
                        iconLeft={!isApproving ? <CheckIcon /> : undefined}
                        data-status-action="approve"
                        data-testid={testId ? `${testId}-approve` : undefined}
                      >
                        Approve
                      </Button>
                    </FlexItem>
                  </Flex>
                </FlexItem>
              )}
            </Flex>

            <QuestionStatusWriteNotice
              issue={statusWriteIssue}
              busy={actionsPending}
              onReapply={onStatusWriteReapply}
              onRefresh={onStatusWriteRefresh}
              onDismiss={onStatusWriteDismiss}
              restoreFocusTo={() =>
                document.querySelector<HTMLElement>(
                  `[data-question-workspace="${question.questionId}"] [data-status-action="approve"], [data-question-workspace="${question.questionId}"] [data-status-action="back"]`
                )
              }
              testId={testId ? `${testId}-status-write` : 'workspace-status-write'}
            />

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
                gap: tokens.dimension.space.md,
                marginTop: tokens.dimension.space.lg,
              }}
            >
              <Box>
                <Text bold marginBottom="xxSmall">
                  Status
                </Text>
                <StatusBadge status={question.status} />
              </Box>
              <Box>
                <Text bold marginBottom="xxSmall">
                  Submitted by
                </Text>
                <Text marginBottom="none">{question.posterName}</Text>
                <Text color="secondary" marginBottom="none">
                  {question.posterEmail}
                </Text>
              </Box>
              <Box>
                <Text bold marginBottom="xxSmall">
                  Submitted
                </Text>
                <Text marginBottom="none">{formatDate(question.createdAt)}</Text>
              </Box>
              <Box>
                <Text bold marginBottom="xxSmall">
                  Quality
                </Text>
                <QualityBadge qualityScore={question.qualityScore} />
              </Box>
            </div>

            <Box marginTop="medium">
              <Text bold>Description</Text>
              <Box
                padding="small"
                backgroundColor="secondary10"
                style={{ borderRadius: tokens.dimension.radius.sm, overflowWrap: 'anywhere' }}
              >
                <Text>{question.description || 'No description provided.'}</Text>
              </Box>
            </Box>

            <ContextSection
              id="images"
              title="Images"
              summary="Review, upload, hide, show, or remove images attached directly to this question."
              isActive={isActive}
              testId={testId ? `${testId}-images` : undefined}
            >
              <QuestionImagesWorkspace
                questionId={question.questionId}
                token={token}
                testId={testId ? `${testId}-images` : undefined}
              />
            </ContextSection>

            <ContextSection
              id="replies"
              title="Replies"
              summary="Moderate replies, add staff responses, and review reply images in context."
              collapseDisabled={replyDraftDirty}
              isActive={isActive}
              testId={testId ? `${testId}-replies` : undefined}
            >
              <ReplyModerationPanel
                questionId={question.questionId}
                questionStatus={question.status}
                token={token}
                onRejectDraftDirtyChange={setReplyDraftDirty}
                parentCloseRequestNonce={closeRequestNonce}
                onParentCloseResolve={handleParentCloseResolve}
                testId={testId ? `${testId}-replies-panel` : undefined}
              />
            </ContextSection>

            <ContextSection
              id="rfi"
              title="Requests for information"
              summary="Create and manage follow-up requests without leaving the question."
              isActive={isActive}
              testId={testId ? `${testId}-rfi` : undefined}
            >
              <RfiManagementPanel
                questionId={question.questionId}
                questionStatus={question.status}
                token={token}
                testId={testId ? `${testId}-rfi-panel` : undefined}
              />
            </ContextSection>

            <ContextSection
              id="changes"
              title="Question changes"
              summary="Review question-level audit events recorded for this question."
              isActive={isActive}
              testId={testId ? `${testId}-changes` : undefined}
            >
              <QuestionChangesTimeline
                questionId={question.questionId}
                token={token}
                testId={testId ? `${testId}-changes-timeline` : undefined}
              />
            </ContextSection>
          </Box>
        </Panel>
      </div>
    );
  }
);
